import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Card,
  Group,
  Title,
  Text,
  Divider,
  UnstyledButton,
  SimpleGrid,
  Loader,
  Collapse,
  SegmentedControl,
} from '@mantine/core';
import { IconChevronRight, IconCards, IconMap } from '@tabler/icons-react';
import { CompetencyCard } from '../competency/CompetencyCard';
import { CompetencyMap } from '../competency/CompetencyMap';
import {
  Competency,
  Swimlane,
  User,
  CardSize,
  ViewMode,
  CompetencyMap as CompetencyMapType,
  Grade,
  UserCompetency,
} from '../../types';
import { CompetencyDrawer } from '../competency/CompetencyDrawer';
import { CardSizeControl } from '../ui/CardSizeControl';
import { useDevice } from '../../hooks/useDevice';

const CompetencyRoadmap = React.lazy(() =>
  import('../competency/roadmap').then((m) => ({ default: m.CompetencyRoadmap })),
);

interface UserProfileCompetenciesProps {
  user: User;
  competencyMap: CompetencyMapType;
  isMapLoading: boolean;
  isOwnProfile: boolean;
}

export const UserProfileCompetencies: React.FC<UserProfileCompetenciesProps> = ({
  user,
  competencyMap,
  isMapLoading,
  isOwnProfile,
}) => {
  const { t } = useTranslation();
  const isMobile = useDevice() === 'mobile';
  // Состояние открытых свимлейнов (используем индексы)
  const [openedSwimlanes, setOpenedSwimlanes] = useState<Set<number>>(new Set([0]));
  const [drawer, setDrawer] = useState<{
    comp: Competency;
    gradeIdx: number;
    swimlane: Swimlane;
  } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [cardSize, setCardSize] = useState<CardSize>('L');

  // Функция для рендера карточек компетенций
  const renderCompetencyCards = () => {
    // Проверяем, есть ли компетенции для отображения
    const hasCompetencies = swimlanes.some((swimlane: Swimlane) => {
      const competencies = competenciesBySwimlane[swimlane._id] || [];
      return competencies.length > 0;
    });

    if (!hasCompetencies) {
      return (
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Group justify="center" py="xl">
            <div style={{ textAlign: 'center' }}>
              <IconCards size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
              <Title order={4} c="dimmed" mb="xs">
                {t('profile.noCompetencies')}
              </Title>
              <Text c="dimmed" size="sm">
                {t('profile.noCompetenciesDescription')}
              </Text>
            </div>
          </Group>
        </Card>
      );
    }

    return (
      <Stack gap="lg">
        {swimlanes.map((swimlane: Swimlane, index: number) => {
          const competencies = competenciesBySwimlane[swimlane._id] || [];
          if (competencies.length === 0) return null;

          return (
            <Card key={swimlane._id} shadow="sm" padding="lg" radius="md" withBorder>
              <UnstyledButton
                onClick={() => {
                  const newOpened = new Set(openedSwimlanes);
                  if (newOpened.has(index)) {
                    newOpened.delete(index);
                  } else {
                    newOpened.add(index);
                  }
                  setOpenedSwimlanes(newOpened);
                }}
                style={{ width: '100%', transition: 'margin-bottom 0.3s ease-in-out' }}
                mb={openedSwimlanes.has(index) ? 'md' : 0}
              >
                <Group justify="space-between">
                  <Title order={4}>{swimlane.title}</Title>
                  <IconChevronRight
                    size={20}
                    style={{
                      transform: openedSwimlanes.has(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </Group>
              </UnstyledButton>

              <Collapse in={openedSwimlanes.has(index)}>
                <Divider mb="md" />
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                  {competencies.map((competency) => {
                    const userCompetency = user.competencies?.find(
                      (uc: UserCompetency) => uc.competencyId === competency._id,
                    );
                    return (
                      <CompetencyCard
                        key={competency._id}
                        comp={competency}
                        gradeIdx={gradeIdxOf(competency._id)}
                        swimlane={swimlane}
                        onClick={() =>
                          setDrawer({
                            comp: competency,
                            gradeIdx: gradeIdxOf(competency._id),
                            swimlane,
                          })
                        }
                        dragProps={{}}
                        dragHandleProps={{}}
                        isDragging={false}
                        userHas={true}
                        userCompetency={userCompetency}
                        isDragDropEnabled={false}
                      />
                    );
                  })}
                </SimpleGrid>
              </Collapse>
            </Card>
          );
        })}
      </Stack>
    );
  };

  // Компетенции пользователя
  const userCompetencyIds = (user?.competencies || []).map((uc: UserCompetency) => uc.competencyId);

  // Собираем все компетенции из карты, которые есть у пользователя
  const userCompetenciesFull: Competency[] = React.useMemo(() => {
    if (!competencyMap) return [];
    return competencyMap.grades
      .flatMap((grade: Grade) => grade.competencies)
      .filter((comp: Competency) => userCompetencyIds.includes(comp._id));
  }, [competencyMap, userCompetencyIds]);

  // Группируем по swimlane
  const swimlanes = React.useMemo(() => competencyMap?.swimlanes || [], [competencyMap?.swimlanes]);
  // Грейд компетенции в карте профиля; -1 — не нашли, карточка грейд не подписывает.
  const gradeIdxOf = (competencyId: string) =>
    (competencyMap?.grades || []).findIndex((g) =>
      g.competencies?.some((c) => c._id === competencyId),
    );
  const competenciesBySwimlane: Record<string, Competency[]> = React.useMemo(() => {
    const map: Record<string, Competency[]> = {};
    for (const swimlane of swimlanes) {
      map[swimlane._id] = userCompetenciesFull.filter((comp) => comp.swimlaneId === swimlane._id);
    }
    return map;
  }, [userCompetenciesFull, swimlanes]);

  if (isMapLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  // Рендерим основной контент в зависимости от профиля
  const renderMainContent = () => {
    if (isOwnProfile) {
      return renderCompetencyCards();
    }

    return (
      <>
        <Group justify="flex-end" align="center" pb="md">
          {/* Переключатель размера карточки - скрываем для вида "карточки" */}
          <div
            style={{
              opacity: viewMode === 'cards' ? 0 : 1,
              visibility: viewMode === 'cards' ? 'hidden' : 'visible',
              transition: 'opacity 0.2s ease, visibility 0.2s ease',
              pointerEvents: viewMode === 'cards' ? 'none' : 'auto',
            }}
          >
            <CardSizeControl value={cardSize} onChange={setCardSize} />
          </div>

          {/* Переключатель вида */}
          <SegmentedControl
            value={viewMode}
            onChange={(value) => setViewMode(value as ViewMode)}
            size="sm"
            data={[
              {
                value: 'map',
                label: (
                  <Group gap="xs" style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
                    <IconMap size={16} style={{ flexShrink: 0 }} />
                    <span style={{ whiteSpace: 'nowrap' }}>{t('profile.map')}</span>
                  </Group>
                ),
              },
              {
                value: 'cards',
                label: (
                  <Group gap="xs" style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
                    <IconCards size={16} style={{ flexShrink: 0 }} />
                    <span style={{ whiteSpace: 'nowrap' }}>{t('profile.cards')}</span>
                  </Group>
                ),
              },
              ...(!isMobile
                ? [
                    {
                      value: 'roadmap',
                      label: (
                        <Group gap="xs" style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
                          <IconMap size={16} style={{ flexShrink: 0 }} />
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {t('competencyMaps.viewRoadmap')}
                          </span>
                        </Group>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Group>

        {/* Отображение в зависимости от выбранного вида */}
        {viewMode === 'roadmap' ? (
          <React.Suspense fallback={<Loader size="sm" />}>
            <CompetencyRoadmap
              competencyMapData={competencyMap}
              competencyMapId={competencyMap?._id}
              userCompetencies={user.competencies || []}
              currentUser={user}
            />
          </React.Suspense>
        ) : viewMode === 'map' ? (
          <CompetencyMap
            isDragDropEnabled={false}
            cardSize={cardSize}
            currentGrade={user.grade}
            nextGrade={undefined}
            competencyMapData={competencyMap}
            competencyMapId={competencyMap?._id}
            userCompetencies={user.competencies || []}
            currentUser={user}
          />
        ) : (
          renderCompetencyCards()
        )}
      </>
    );
  };

  return (
    <>
      {renderMainContent()}

      {/* Дровер компетенции - один для всех случаев */}
      <CompetencyDrawer
        drawer={drawer}
        onClose={() => setDrawer(null)}
        grades={(competencyMap?.grades || []).map((g) => g.title)}
        currentUser={user}
        userCompetencies={user.competencies || []}
        competencyMapId={competencyMap?._id}
        hideStatusAndActions={!isOwnProfile}
      />
    </>
  );
};
